import React from "react";
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { Close, History, Lock } from "@mui/icons-material";
import dayjs from "dayjs";

// =============================================================================
// TIPOS - Interfaces
// =============================================================================

/**
 * Item do histórico
 */
export interface HistoryItem {
  id: string;
  date: string;
  value?: string | boolean; // Valor principal pode ser opcional
  practitioner: string;
  additionalInfo?: string; // Informações adicionais (descrições, observações)
}

/**
 * Props do componente HistoryModal
 */
interface HistoryModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  history: HistoryItem[];
}

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================

/**
 * Modal para exibir histórico de um campo específico
 * 
 * @param open - Se o modal está aberto
 * @param onClose - Função para fechar o modal
 * @param title - Título do histórico
 * @param history - Array de itens do histórico
 */
export const HistoryModal: React.FC<HistoryModalProps> = ({
  open,
  onClose,
  title,
  history,
}) => {
  /**
   * Formata data para exibição
   */
  const formatDate = (dateString: string): string => {
    return dayjs(dateString).format("DD/MM/YYYY HH:mm");
  };

  /**
   * Formata valor para exibição, incluindo informações adicionais
   */
  const formatValue = (value: string | boolean | undefined, additionalInfo?: string): string => {
    let mainValue = "";
    
    // Formata o valor principal
    if (typeof value === "boolean") {
      mainValue = value ? "Sim" : "Não";
    } else if (value !== undefined && value !== null && value !== "") {
      mainValue = String(value);
    }
    
    // Concatena informações adicionais se existirem
    if (additionalInfo) {
      if (mainValue) {
        return `${mainValue}\n\n${additionalInfo}`;
      }
      return additionalInfo;
    }
    
    return mainValue || "-";
  };

  /**
   * Verifica se um item possui assinatura digital
   * Detecta o emoji de cadeado no início do valor
   */
  const hasSignedItem = (value: string | boolean | undefined): boolean => {
    if (typeof value === "string") {
      return value.startsWith("🔒 ");
    }
    return false;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: "rgba(0, 168, 132, 0.05)",
          borderBottom: "1px solid rgba(0, 168, 132, 0.2)",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <History sx={{ color: "#00A884" }} />
            <Typography variant="h6" fontWeight={600}>
              {title}
            </Typography>
            <Chip
              label={`${history.length} registro${history.length !== 1 ? "s" : ""}`}
              size="small"
              sx={{
                bgcolor: "#00A884",
                color: "#fff",
                fontSize: "0.7rem",
                height: 20,
                fontWeight: 600,
              }}
            />
          </Stack>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {history.length > 0 ? (
          <Stack spacing={2}>
            {history.map((item, index) => (
              <Paper
                key={item.id}
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: index === 0 ? "rgba(0, 168, 132, 0.05)" : "#fafafa",
                  border: "1px solid",
                  borderColor: index === 0 ? "#00A884" : "#e0e0e0",
                  borderRadius: 2,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: "#00A884",
                    boxShadow: "0 2px 8px rgba(0, 168, 132, 0.15)",
                  },
                }}
              >
                <Stack spacing={1}>
                  {/* Cabeçalho com data e profissional */}
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={1}
                  >
                    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        color="#00A884"
                        sx={{ fontSize: "0.8rem" }}
                      >
                        {formatDate(item.date)}
                      </Typography>
                      {index === 0 && (
                        <Chip
                          label="Mais recente"
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: "0.65rem",
                            bgcolor: "#00A884",
                            color: "#fff",
                          }}
                        />
                      )}
                      {hasSignedItem(item.value) && (
                        <Chip
                          icon={<Lock sx={{ fontSize: 12, color: "#fff !important" }} />}
                          label="Assinado"
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: "0.65rem",
                            bgcolor: "#1976d2",
                            color: "#fff",
                            fontWeight: 600,
                            "& .MuiChip-icon": {
                              marginLeft: "4px",
                            },
                          }}
                        />
                      )}
                    </Stack>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: "0.75rem" }}
                    >
                      {item.practitioner}
                    </Typography>
                  </Stack>

                  <Divider />

                  {/* Conteúdo do registro */}
                  <Typography
                    variant="body2"
                    color="text.primary"
                    sx={{ whiteSpace: "pre-wrap" }}
                  >
                    {formatValue(item.value, item.additionalInfo)}
                  </Typography>
                </Stack>
              </Paper>
            ))}
          </Stack>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 6,
            }}
          >
            <History sx={{ fontSize: 64, color: "#bdbdbd", mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Nenhum registro anterior
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Este é o primeiro registro deste campo para este pet
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default HistoryModal;

